import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

/**
 * Authentication guard for protected routes
 */
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  // Initialize auth state if not already done
  if (!authStore.isInitialized) {
    await authStore.initialize();
  }

  const requiresAuth = to.matched.some(record => record.meta?.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta?.requiresGuest);
  const requiresVerification = to.matched.some(record => record.meta?.requiresVerification);

  // If user is authenticated and trying to access guest-only pages
  if (requiresGuest && authStore.isAuthenticated) {
    const redirectPath = (to.query.redirect as string) || '/app';
    next(redirectPath);
    return;
  }

  // If route requires authentication and user is not authenticated
  if (requiresAuth && !authStore.isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    });
    return;
  }

  // If route requires email verification and user is not verified
  if (requiresVerification && authStore.isAuthenticated && !authStore.isVerified) {
    next({ name: 'verify-email' });
    return;
  }

  // Continue navigation
  next();
}

/**
 * Global navigation guard for setting page titles
 */
export function titleGuard(to: RouteLocationNormalized) {
  const baseTitle = 'ASAB - Advanced Self-Aware Bots';

  if (to.meta.title) {
    document.title = `${to.meta.title} | ${baseTitle}`;
  } else {
    document.title = baseTitle;
  }
}

/**
 * Permission guard for role-based access control
 */
export function permissionGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const requiredPermissions = to.meta?.permissions as string[] | undefined;
  const requiredRole = to.meta?.role as string | undefined;

  // If no specific permissions required, continue
  if (!requiredPermissions && !requiredRole) {
    next();
    return;
  }

  // Check role-based access
  if (requiredRole && (!authStore.user?.role || authStore.user.role !== requiredRole)) {
    next({ name: 'not-found' }); // or redirect to access denied page
    return;
  }

  // Check permission-based access. Permissions come from /me (resolved per
  // request by the gateway from the org role; an "admin" grant is expanded to
  // the full set server-side). Enforce only when we actually have a permission
  // list — when absent (e.g. a profile persisted before this field existed),
  // defer to the backend, which is the real authority. Client gating is UX-only.
  const userPermissions = authStore.user?.permissions;
  if (requiredPermissions && userPermissions && userPermissions.length > 0) {
    const hasPermission =
      userPermissions.includes('admin') ||
      requiredPermissions.every(permission => userPermissions.includes(permission));

    if (!hasPermission) {
      next({ name: 'not-found' }); // or redirect to access denied page
      return;
    }
  }

  next();
}

/**
 * Admin route guard
 */
export function adminGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  if (!authStore.user?.role || authStore.user.role !== 'admin') {
    next({ name: 'not-found' });
    return;
  }

  next();
}

/**
 * Verified user guard
 */
export function verifiedGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }

  if (!authStore.isVerified) {
    next({ name: 'verify-email' });
    return;
  }

  next();
}
